import React from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippy.js/react';
import { useTranslation } from 'react-i18next';
import { IconConnector } from 'svgIcons/MotionPortalIcons';
import { getAssetIcon } from 'helpers/icons';
import colors from 'theme/_colors.scss';

import { GEARBOX_ASSET_TYPE } from './constants';
import './PhysicalConnectionFlow.scss';

const ICON_SIZE = { width: 24, height: 24, color: colors.doveGray };

/**
 * PhysicalConnectionFlow - Shared component for displaying a horizontal row of
 * physically connected assets with optional sub-type / speed-ratio details.
 *
 * Card design is identical to PowertrainAssetsConfiguration:
 *   - grey .asset-header-section (icon + name) on top
 *   - white .asset-details-section (subType / speedRatio) on bottom, only when data exists
 *   - .connection-arrow SVG between cards, vertically centred via align-items: stretch
 *
 * @param {Array}   assets         - Asset objects to display
 * @param {*}       currentAssetId - Highlights the matching card with a blue border
 * @param {string}  className      - Extra CSS class for the root element
 */
const PhysicalConnectionFlow = ({
    assets = [],
    currentAssetId = null,
    className = ''
}) => {
    const { t: translate } = useTranslation();

    if (!assets?.length) {
        return null;
    }

    return (
        <div className={['physical-connection-flow', className].filter(Boolean).join(' ')} id='physical-connection-flow'>
            <div className='flow-scroll-wrapper'>
                <div className='assets-flow'>
                    {assets.map((asset, index) => {
                        const assetId = asset?.id;
                        const isCurrent = currentAssetId !== null && currentAssetId !== undefined && String(assetId) === String(currentAssetId);
                        const assetSubType = asset?.subType;
                        const hasDetails = assetSubType || asset?.outputSpeedRatio;
                        const displayName = asset?.assetName || '-';

                        return (
                            <React.Fragment key={assetId ?? index}>
                                {/* ── Asset card: grey header + white details ── */}
                                <div className='asset-item-horizontal' id={`asset-item-${assetId}`} aria-current={isCurrent ? 'true' : 'false'}>
                                    {/* Grey section: icon + name row */}
                                    <div className={['asset-header-section', isCurrent ? 'is-current' : ''].filter(Boolean).join(' ')}>
                                        <div className='asset-icon'>
                                            {getAssetIcon(assetSubType || asset?.assetType, ICON_SIZE)}
                                        </div>
                                        <div className='asset-name-row'>
                                            <Tippy
                                                content={displayName}
                                                animation='scale'
                                                theme='light-border'
                                                trigger='mouseenter focus'
                                                placement='top'
                                                maxWidth={280}
                                            >
                                                <div className='asset-name'>
                                                    {displayName}
                                                </div>
                                            </Tippy>
                                        </div>
                                    </div>

                                    {/* White section: subType / speedRatio — only when present */}
                                    {hasDetails &&
                                        <div className='asset-details-section' id={`asset-details-${assetId}`}>
                                            <div className='asset-details'>
                                                <div className='asset-meta'>
                                                    {assetSubType &&
                                                        <div className='sub-type'>
                                                            {translate('ABB.Powertrain.Frontend.assetDetailsPhysicalConnectionSubTypeLabel')}: <span className='value'>{assetSubType}</span>
                                                        </div>
                                                    }
                                                    {asset?.outputSpeedRatio && assetSubType?.toLowerCase() === GEARBOX_ASSET_TYPE &&
                                                        <div className='speed-ratio'>
                                                            {translate('ABB.Powertrain.Frontend.assetDetailsPhysicalConnectionSpeedRatioLabel')}: <span className='value'>{asset.outputSpeedRatio}</span>
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>

                                {/* ── Connector between cards ── */}
                                {index < assets.length - 1 &&
                                    <div className='connection-arrow' aria-hidden='true' id={`connection-arrow-${assetId}`}>
                                        <IconConnector width={20} height={20} />
                                    </div>
                                }
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

PhysicalConnectionFlow.propTypes = {
    assets: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            assetName: PropTypes.string,
            assetType: PropTypes.string,
            subType: PropTypes.string,
            outputSpeedRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
    ),
    currentAssetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string
};

export default PhysicalConnectionFlow;
